import { cx } from '@flowus/common/cx';
import { UserAuthorizationType } from '@next-space/fe-api-idl';
import { split } from 'lodash-es';
import type { ChangeEvent, FC } from 'react';
import { useEffect, useState } from 'react';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { Avatar } from 'src/common/components/avatar';
import { Button } from 'src/common/components/button';
import { Input } from 'src/common/components/input';
import { message } from 'src/common/components/message';
import { useOpenModal } from 'src/common/components/next-modal';
import { useModel } from 'src/common/create-model';
import { SettingPassword, SettingPasswordType } from 'src/components/login-uikit/setting-password';
import { useUpload } from 'src/hooks/space/use-upload';
import { useCurrentUser, useLogout, useUpdateUser } from 'src/hooks/user';
import { useFetchMe } from 'src/hooks/user/use-fetch-me';
import { MessageIds } from 'src/modals';
import { bindDataTestId, TestIds } from 'src/utils/qa-utils';
import { useOpenLogoffDialog } from 'src/views/logoff';
import { SettingModalCommon, SettingProvider } from '../common';
import { useOpenBindAccountDialog } from '../use-open-bind-account-dialog';
import { useCloseSettingModal } from '../use-open-setting-modal';
import { AccountPhone } from './account-phone';
import { Authentication } from './authentication';
import { useOpenAccountPin } from './account-pin';
import { AccountEmail } from './email';

/** 账户设置 */
export const AccountSetting: FC = () => {
  const { t } = useAppTranslation();
  const logout = useLogout();
  const closeSettingModal = useCloseSettingModal();
  const currentUser = useCurrentUser();
  const updateUser = useUpdateUser();
  const [userName, setUserName] = useState(currentUser.nickname);
  const [avatar, setAvatar] = useState(currentUser.avatar || '');
  const { setClickMaskClose } = useModel(SettingProvider);
  const upload = useUpload();
  const openLogoffDialog = useOpenLogoffDialog();
  const openModal = useOpenModal();
  const openBindAccountDialog = useOpenBindAccountDialog();
  const openAccountPin = useOpenAccountPin();
  const fetchMe = useFetchMe();

  const saveUserName = async () => {
    const nickname = userName.trim();

    if (split(nickname, '').length >= 2 && split(nickname, '').length <= 55) {
      await updateUser({ nickname, avatar });
      closeSettingModal();
      message.success(t('common.save_success') as string);
    } else {
      message.error(t('views.settings.account.nickname_length_error') as string);
    }
  };

  const processFile = (e: ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const file = fileList[0]!;
    message.loading({ key: MessageIds.UPLOAD_ICON, content: '上传中...' });
    void upload({
      file,
      type: 'public',
      searchType: 'avatar',
      onComplete: (ret) => {
        message.closeMessage(MessageIds.UPLOAD_ICON);
        if (ret.success) {
          e.target.value = '';
          message.success('上传完成');
          setAvatar(ret.ossName);
        }
      },
    });
  };

  const openSettingPassword = () => {
    openModal.modal({
      content({ onCloseModal }) {
        return (
          <SettingPassword
            phone={currentUser.phone || currentUser.email}
            type={
              currentUser.password
                ? SettingPasswordType.BY_OLD_PASSWORD
                : SettingPasswordType.BY_ACCOUNT
            }
            onSuccess={async () => {
              await fetchMe();
              message.success(t('common.set_success') as string);
              onCloseModal();
              message.success(t('views.settings.account.login_expired') as string);
              void logout();
            }}
          />
        );
      },
    });
  };

  useEffect(() => {
    setClickMaskClose(currentUser.nickname === userName && avatar === (currentUser.avatar || ''));
  }, [userName, currentUser.nickname, setClickMaskClose, currentUser.avatar, avatar]);

  return (
    <>
      <div className={SettingModalCommon.commonStyles.container}>
        {/* 头像 */}
        <SettingModalCommon.Header
          title={t('common.account_settings') as string}
          name={t('views.settings.account.avatar') as string}
        />
        <div className="mt-4 flex flex-row items-center justify-between">
          <Avatar
            name={currentUser.nickname}
            color={currentUser.backgroundColor}
            icon={{ type: 'upload', value: avatar }}
            className="rounded-full mb-3.5 text-white"
            imgClassName="rounded-full"
            iconSize={60}
            style={{ fontSize: '30px' }}
          />
          <div className="flex flex-row space-x-5">
            <Button className={cx({ hidden: !avatar })} onClick={() => setAvatar('')}>
              {t('views.settings.account.remove_avatar')}
            </Button>
            <Button className="px-0">
              <label className="cursor-pointer py-1.5 px-4">
                {t('views.settings.account.upload_avatar')}
                <input className="hidden" type="file" accept="image/*" onChange={processFile} />
              </label>
            </Button>
          </div>
        </div>
        <SettingModalCommon.SettingDivider className="mb-2.5" />

        {/* 昵称 */}
        <div className={SettingModalCommon.commonStyles.name}>
          {t('views.settings.account.nickname')}
        </div>
        <Input
          maxLength={55}
          className={SettingModalCommon.commonStyles.input}
          value={userName}
          inputClassName="h-8"
          placeholder={t('views.settings.account.nickname_placeholder') as string}
          {...bindDataTestId(TestIds.settingAccountInput)}
          onEnter={() => saveUserName()}
          onChange={(value) => setUserName(`${value}`)}
        />
        <SettingModalCommon.SettingDivider />
        {!__PRIVATE__ && (
          <>
            <AccountEmail />
            <SettingModalCommon.SettingDivider />
          </>
        )}
        {/* 手机号 */}
        {!__BUILD_IN__ && (
          <>
            <AccountPhone />
            <SettingModalCommon.SettingDivider />
          </>
        )}

        {/* 学生认证 */}
        {!(__BUILD_IN__ || __PRIVATE__) && (
          <>
            <Authentication />
            <SettingModalCommon.SettingDivider />
          </>
        )}

        {/* 密码 */}
        <>
          <SettingModalCommon.Item
            leftChild={t('views.settings.account.login_password')}
            rightChild={
              <Button onClick={openSettingPassword}>
                {currentUser.password ? t('common.change_password') : t('common.set_password')}
              </Button>
            }
          />
          <SettingModalCommon.SettingDivider />
        </>

        {/* PIN */}
        <>
          <SettingModalCommon.Item
            leftChild={
              <div className="space-y-0.5">
                <div>PIN</div>
                <div className="text-t4 text-grey3">
                  {t('views.settings.account.lock_screen_pin')}
                </div>
              </div>
            }
            rightChild={
              <Button onClick={openAccountPin}>
                {currentUser.setting?.pin
                  ? t('views.settings.account.change_pin')
                  : t('views.settings.account.add_pin')}
              </Button>
            }
          />
          <SettingModalCommon.SettingDivider />
        </>

        {/* 邮箱 */}
        {!__BUILD_IN__ && !__PRIVATE__ && (
          <>
            <SettingModalCommon.Item
              leftChild={
                <div className="space-y-0.5">
                  <div key="email">{t('common.email')}</div>
                  {currentUser.ext?.email && (
                    <div className="text-t4 text-grey3">{currentUser.ext.email.id}</div>
                  )}
                </div>
              }
              rightChild={
                <Button
                  onClick={(e) => {
                    openBindAccountDialog(e, UserAuthorizationType.EMAIL);
                  }}
                >
                  {currentUser.ext?.email
                    ? t('views.settings.account.unbind')
                    : t('common.bind_email')}
                </Button>
              }
            />
            <SettingModalCommon.SettingDivider />
          </>
        )}

        {/* 微信 */}
        {!(__BUILD_IN__ || __PRIVATE__) && (
          <>
            <SettingModalCommon.Item
              leftChild={
                <div className="space-y-0.5">
                  <div>{t('common.wechat')}</div>
                  {currentUser.ext?.wechat && (
                    <div className="text-t4 text-grey3">{currentUser.ext?.wechat?.nickname}</div>
                  )}
                </div>
              }
              rightChild={
                <Button
                  onClick={(e) => {
                    openBindAccountDialog(e, UserAuthorizationType.WECHAT);
                  }}
                >
                  {currentUser.ext?.wechat
                    ? t('views.settings.account.unbind')
                    : t('common.bind_wechat')}
                </Button>
              }
            />
            <SettingModalCommon.SettingDivider />
          </>
        )}

        {/* QQ */}
        {!(__BUILD_IN__ || __PRIVATE__) && (
          <>
            <SettingModalCommon.Item
              leftChild={
                <div className="space-y-0.5">
                  <div>QQ</div>
                  {currentUser.ext?.qq?.nickname && (
                    <div className="text-t4 text-grey3">{currentUser.ext.qq.nickname}</div>
                  )}
                </div>
              }
              rightChild={
                <Button
                  onClick={(e) => {
                    openBindAccountDialog(e, UserAuthorizationType.QQ);
                  }}
                >
                  {currentUser.ext?.qq
                    ? t('views.settings.account.unbind')
                    : t('views.settings.account.bind_qq')}
                </Button>
              }
            />
            <SettingModalCommon.SettingDivider />
          </>
        )}

        {/* APPLE ID */}
        {!__PRIVATE__ && (
          <>
            <SettingModalCommon.Item
              leftChild={
                <div className="space-y-0.5">
                  <div>Apple ID</div>
                  {currentUser.ext?.apple && (
                    <div className="text-t4 text-grey3">
                      {currentUser.ext?.apple.nickname ||
                        currentUser.ext?.apple.email ||
                        currentUser.ext?.apple.id}
                    </div>
                  )}
                </div>
              }
              rightChild={
                <Button
                  onClick={(e) => {
                    openBindAccountDialog(e, UserAuthorizationType.APPLE);
                  }}
                >
                  {currentUser.ext?.apple
                    ? t('views.settings.account.unbind')
                    : t('views.settings.account.bind_apple_id')}
                </Button>
              }
            />
            <SettingModalCommon.SettingDivider />
          </>
        )}

        <SettingModalCommon.Item
          leftChild={
            <div className="space-y-0.5">
              <div>{t('views.settings.account.delete_account')}</div>
              <div className="text-t4 text-grey3">
                {t('views.settings.account.delete_account_description')}
              </div>
            </div>
          }
          rightChild={
            <Button
              colorType="red"
              onClick={() => {
                void openLogoffDialog();
              }}
            >
              {t('views.settings.account.delete_account')}
            </Button>
          }
        />
      </div>
      <SettingModalCommon.FooterBar cancel={() => closeSettingModal()} confirm={saveUserName} />
    </>
  );
};
